import { useCallback, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import adminService from '@wlb/shared/api/admin/adminService';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Avatar } from '../components/ui/Avatar';
import { Icon } from '../components/ui/Icon';
import { IconButton } from '../components/ui/IconButton';
import { StateBlock } from '../components/ui/StateBlock';
import { StatusPill } from '../components/ui/StatusPill';
import { TabNav, type TabNavItem } from '../components/ui/TabNav';
import { useAsyncResource } from '../hooks/useAsyncResource';
import { TemporaryPasswordDialog } from '../features/users/TemporaryPasswordDialog';
import type { UserDetailContext } from '../features/users/userForm';
import { resolvePictureUrl } from '../utils/resolveImageUrl';
import '../components/ui/ui.css';
import './pages.css';

/**
 * Member detail shell.
 *
 * The record is loaded once here and handed to the sub-modules through the
 * outlet, so moving between profile, posts, sign-ins and activation swaps only
 * the panel below the header: no refetch, no spinner, no scroll jump. Each
 * sub-module is a real route, so it is bookmarkable and the back button walks
 * back through the tabs the operator opened.
 */
export function UserDetails() {
  const { t } = useTranslation('admin');
  const { userGuid = '' } = useParams<{ userGuid: string }>();
  const location = useLocation();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [statusPending, setStatusPending] = useState(false);

  const load = useCallback(() => adminService.getUser(userGuid), [userGuid]);
  const { data, status, refreshing, error, reload } = useAsyncResource(
    load,
    userGuid,
    t('common.errorTitle'),
    true,
    'user',
  );

  /**
   * The list hands over the filters it was showing, so "back to buddies" returns
   * to the exact page and search the operator left instead of a reset table.
   */
  const listSearch = (location.state as { listSearch?: string } | null)?.listSearch;
  const backTo = `/users${listSearch ?? ''}`;

  const toggleActive = async () => {
    if (!data) {
      return;
    }
    setStatusPending(true);
    try {
      await adminService.setUserActiveStatus(data.userGuid, !data.isActive);
      ToastService.show({
        type: 'success',
        message: data.isActive
          ? t('users.toasts.deactivated')
          : t('users.toasts.activated'),
      });
      reload();
    } catch (cause) {
      ToastService.show({
        type: 'error',
        message: getUserFacingErrorMessage(cause, t('common.errorTitle')),
      });
    } finally {
      setStatusPending(false);
    }
  };

  const tabs = useMemo<readonly TabNavItem[]>(
    () => [
      {
        to: `/users/${userGuid}`,
        label: t('users.details.tabs.profile'),
        icon: 'users',
        tone: 'blue',
        end: true,
      },
      {
        to: `/users/${userGuid}/posts`,
        label: t('users.details.tabs.posts'),
        icon: 'post',
        tone: 'teal',
      },
      {
        to: `/users/${userGuid}/logins`,
        label: t('users.details.tabs.logins'),
        icon: 'history',
        tone: 'indigo',
      },
      {
        to: `/users/${userGuid}/activation`,
        label: t('users.details.tabs.activation'),
        icon: 'power',
        tone: 'emerald',
      },
    ],
    [t, userGuid],
  );

  const displayName = data
    ? [data.firstName, data.lastName].filter(Boolean).join(' ') ||
      data.userName ||
      t('common.none')
    : t('users.details.title');

  const context: UserDetailContext | null = data ? { user: data, reload } : null;

  return (
    <div className="page">
      <header className="page__header page__header--entity">
        {data ? (
          <div className="page__identity">
            <Avatar src={resolvePictureUrl(data.picture)} name={displayName} size="md" />
            <div className="page__identity-text">
              <h1 className="page__title">{displayName}</h1>
              <p className="page__subtitle">
                {data.email || data.userName || t('common.none')}
              </p>
              <div className="page__badges">
                {data.isUserBlocked ? (
                  <StatusPill label={t('status.blocked')} tone="danger" />
                ) : (
                  <StatusPill
                    label={data.isActive ? t('status.active') : t('status.inactive')}
                    tone={data.isActive ? 'success' : 'neutral'}
                  />
                )}
                <StatusPill
                  label={
                    data.isEmailVerified ? t('status.verified') : t('status.unverified')
                  }
                  tone={data.isEmailVerified ? 'info' : 'warning'}
                />
                <StatusPill
                  label={data.role ? t(`roles.${data.role}`) : t('roles.unknown')}
                  tone="neutral"
                />
              </div>
            </div>
          </div>
        ) : (
          <div>
            <h1 className="page__title">{t('users.details.title')}</h1>
            <p className="page__subtitle">{t('users.subtitle')}</p>
          </div>
        )}

        <div className="page__header-actions">
          <Link className="ui-button ui-button--secondary ui-button--small" to={backTo}>
            <Icon name="arrowLeft" size="sm" />
            {t('users.details.back')}
          </Link>
          {data ? (
            <>
              <IconButton
                icon="key"
                tone="amber"
                label={t('users.actions.temporaryPassword')}
                onClick={() => setPasswordDialogOpen(true)}
              />
              <IconButton
                icon={data.isActive ? 'ban' : 'power'}
                tone={data.isActive ? 'rose' : 'emerald'}
                disabled={statusPending}
                label={
                  data.isActive
                    ? t('users.actions.deactivate')
                    : t('users.actions.activate')
                }
                onClick={toggleActive}
              />
            </>
          ) : null}
          <IconButton
            icon="refresh"
            tone="slate"
            label={t('common.refresh')}
            disabled={refreshing}
            onClick={reload}
          />
        </div>
      </header>

      {status === 'loading' ? <StateBlock status="loading" /> : null}
      {status === 'error' ? (
        <StateBlock status="error" message={error} onRetry={reload} />
      ) : null}

      {context ? (
        <>
          <TabNav ariaLabel={t('users.details.tabsLabel')} items={tabs} />
          {/* aria-busy, not a spinner: a background refresh must not take the
              panel the operator is reading away from them. */}
          <div className="page__tab-panel" aria-busy={refreshing || undefined}>
            <Outlet context={context} />
          </div>
        </>
      ) : null}

      <TemporaryPasswordDialog
        open={passwordDialogOpen}
        userGuid={userGuid}
        onClose={() => setPasswordDialogOpen(false)}
      />
    </div>
  );
}
